import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { PageHeader } from '@/components/common/PageHeader';
import { Spinner } from '@/components/common/Spinner';
import { ErrorState } from '@/components/common/ErrorState';
import { useToast } from '@/components/common/toast';
import { ApiError } from '@/api/client/abpError';
import type { TenantConfigurationDto } from '@/api/types/tenantConfiguration';
import { useTenantConfiguration, useUpdateTenantConfiguration } from './api';

const schema = z.object({
  instrumentProfitabilityIncludeLP: z.boolean(),
  clientProfitabilityExcludeDisabledLps: z.boolean(),
  enableSwapFreeCharge: z.boolean(),
  enableGroupMarginRateControl: z.boolean(),
  equityOnlyRelevant: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

// Order and labels mirror the legacy Admin/TenantConfiguration/Index.cshtml
// (finkit-checkbox type="Switch") and Finkit en.json.
const SWITCHES: { name: keyof FormValues; id: string; label: string }[] = [
  {
    name: 'instrumentProfitabilityIncludeLP',
    id: 'instrumentProfitabilityIncludeLP',
    label: 'Include LP in Instrument Profitability',
  },
  {
    name: 'clientProfitabilityExcludeDisabledLps',
    id: 'clientProfitabilityExcludeDisabledLps',
    label: 'Exclude Disabled LPs in Client Profitability',
  },
  {
    name: 'enableSwapFreeCharge',
    id: 'enableSwapFreeCharge',
    label: 'Enable Swap Free Charge',
  },
  {
    name: 'enableGroupMarginRateControl',
    id: 'enableGroupMarginRateControl',
    label: 'Enable Group Margin Rate Control',
  },
  {
    name: 'equityOnlyRelevant',
    id: 'equityOnlyRelevant',
    label: 'Equity Only Relevant',
  },
];

const EMPTY: FormValues = {
  instrumentProfitabilityIncludeLP: false,
  clientProfitabilityExcludeDisabledLps: false,
  enableSwapFreeCharge: false,
  enableGroupMarginRateControl: false,
  equityOnlyRelevant: false,
};

export function TenantConfigurationPage() {
  const toast = useToast();
  const query = useTenantConfiguration();
  const updateMut = useUpdateTenantConfiguration();

  const { register, handleSubmit, reset } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: EMPTY,
  });

  useEffect(() => {
    if (query.data) reset(query.data);
  }, [query.data, reset]);

  function onSubmit(values: FormValues) {
    updateMut.mutate(values as TenantConfigurationDto, {
      onSuccess: () => {
        toast.success('Settings saved successfully.');
        reset(values);
      },
      onError: (err) =>
        toast.error(err instanceof ApiError ? err.message : 'Failed to save the settings.'),
    });
  }

  if (query.isLoading) return <Spinner />;
  if (query.isError)
    return <ErrorState message="Could not load the configuration." onRetry={() => query.refetch()} />;

  return (
    <>
      <PageHeader title="Configuration" breadcrumb={['Home', 'Configuration']} />
      <div className="card">
      <div className="card-header border-0">
        <h5 className="card-title mb-0">Configuration</h5>
      </div>
      <div className="card-body">
        <form id="tenantConfigurationForm" onSubmit={handleSubmit(onSubmit)}>
          <div className="row">
            {SWITCHES.map((s) => (
              <div key={s.id} className="col-lg-6 form-group">
                <div className="form-check form-switch form-check-md">
                  <input
                    className="form-check-input me-3"
                    type="checkbox"
                    role="switch"
                    id={s.id}
                    {...register(s.name)}
                  />
                  <label className="form-check-label form-label" htmlFor={s.id}>
                    {s.label}
                  </label>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3">
            <button
              type="submit"
              id="tenantConfigurationSaveBtn"
              className="btn btn-primary fs-6"
              disabled={updateMut.isPending}
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
    </>
  );
}
